import { Cross2Icon, TargetIcon } from '@radix-ui/react-icons';
import { Badge, Box, Button, Card, Flex, Text } from '@radix-ui/themes';
import { InstallHelper } from 'classes/helpers/install.helper';
import {
  BaseballIcon,
  MachineIcon,
} from 'components/common/custom-icon/shorthands';
import { CommonConfirmBallType } from 'components/common/dialogs/confirm-ball-type';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonNavMenu } from 'components/common/layout/nav-menu';
import { InstallationContext } from 'components/machine/dialogs/installation/context';
import { SidebarNavigationTrigger } from 'components/main/sidebar/nav-trigger';
import { MachineContext, MachineDialogMode } from 'contexts/machine.context';
import { t } from 'i18next';
import { InstallStep } from 'lib_ts/enums/installation';
import { BallType, InstallStepStatus } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { Nullish } from 'lib_ts/interfaces/common/i-generics';
import { IInstallationDetails } from 'lib_ts/interfaces/i-machine';
import { ReactNode, useCallback, useContext, useMemo, useState } from 'react';

interface IProps {
  install: IInstallationDetails;
  expanded: boolean;
}

interface IReminder {
  icon: ReactNode;
  label: string;
  currentStep: number;
  totalSteps: number;
}

const getLabel = (step: Nullish<InstallStep>, ballType: Nullish<BallType>) => {
  switch (step) {
    case InstallStep.Repeatability: {
      return 'common.repeatability-test';
    }

    case InstallStep.BallType: {
      return `${ballType ?? 'Ball Type'} Calibration`;
    }

    default: {
      return 'common.machine-install';
    }
  }
};

const getIcon = (step: Nullish<InstallStep>) => {
  switch (step) {
    case InstallStep.BallType: {
      return <BaseballIcon />;
    }

    case InstallStep.Repeatability: {
      return <TargetIcon />;
    }

    default: {
      return <MachineIcon />;
    }
  }
};

export const InstallReminder = (props: IProps) => {
  const { machine, openDialog } = useContext(MachineContext);
  const { tryCancel } = useContext(InstallationContext);

  const { install } = props;

  const shape = useMemo<IReminder>(() => {
    const steps = InstallHelper.getSteps(install, machine);

    const incompleteIdx = steps.findIndex(
      (m) => !m.status || m.status === InstallStepStatus.Incomplete
    );

    const output: IReminder = {
      label: getLabel(install.standalone, install.ball_type),
      icon: getIcon(install.standalone),
      currentStep: incompleteIdx + 1,
      totalSteps: steps.length,
    };

    return output;
  }, [install, machine]);

  // remind user to change balls in hopper
  const [dialogBall, setDialogBall] = useState<number>();

  const handleClick = useCallback(() => {
    if (!install) {
      return;
    }

    if (install.ball_type !== machine.ball_type) {
      // show alert re: changing ball type first
      setDialogBall(Date.now());
      return;
    }

    openDialog(MachineDialogMode.Installation);
  }, [install, machine.ball_type]);

  const content = useMemo(() => {
    const tooltip = `${t('common.hardware-diagnostic')}: ${
      install?.ball_type ?? '???'
    }`;

    if (!props.expanded) {
      return (
        <CommonNavMenu title={tooltip}>
          <SidebarNavigationTrigger
            label={shape.label}
            icon={shape.icon}
            onClick={handleClick}
            active
          />
        </CommonNavMenu>
      );
    }

    return (
      <Card title={tooltip}>
        <Flex direction="column" gap="2">
          <Flex align="center" gap="2">
            <Box className="valign-center" pt="1">
              {shape.icon}
            </Box>

            <Box
              flexGrow="1"
              overflow="hidden"
              style={{ textOverflow: 'ellipsis' }}
            >
              <Text truncate>{t(shape.label)}</Text>
            </Box>

            {!install?.standalone && (
              <Badge variant="soft" color={RADIX.COLOR.NEUTRAL}>
                {shape.currentStep} / {shape.totalSteps}
              </Badge>
            )}
          </Flex>

          <Flex gap="2">
            <Button color="gray" variant="soft" onClick={() => tryCancel()}>
              <Box className="valign-center" pt="1">
                <Cross2Icon />
              </Box>
            </Button>
            <Button style={{ flex: 1 }} onClick={handleClick}>
              {t('common.resume')}
            </Button>
          </Flex>
        </Flex>
      </Card>
    );
  }, [props.expanded, install, shape, handleClick, tryCancel]);

  return (
    <ErrorBoundary identifier="InstallReminder">
      {content}

      {dialogBall && install && (
        <CommonConfirmBallType
          key={dialogBall}
          nextBallType={install.ball_type}
          onContinue={() => {
            setDialogBall(undefined);
            openDialog(MachineDialogMode.Installation);
          }}
        />
      )}
    </ErrorBoundary>
  );
};
